// The words an ApiError shows. Kept free of the `@/lib/api` import chain so it
// can be unit-tested under plain `node:test`.
//
// Many routes answer { error: "<code>", code: "<code>" } with no message (the
// brain proxy's brain_unreachable, for one). Those codes are for the client to
// branch on, never to show: a code this file knows gets a sentence, an unknown
// one falls back to the status sentence.

export const BRAIN_UNREACHABLE_MESSAGE =
  "This Boo is still starting up or can't be reached right now. Try again in a minute.";

const CODE_MESSAGES: Record<string, string> = {
  brain_unreachable: BRAIN_UNREACHABLE_MESSAGE,
  brain_auth: "Can't reach this Boo right now. Try again in a minute, and if it keeps happening let support know.",
  brain_outdated: "This Boo needs an update before it can do this. It updates automatically, usually within a few days.",
  not_found: "This was changed or removed. Refresh the page and try again.",
};

const STATUS_MESSAGES: Record<number, string> = {
  401: "You need to sign in to continue.",
  403: "You don't have permission to do this.",
  404: "The resource you're looking for doesn't exist.",
  409: "This conflicts with the current state. Try refreshing.",
  429: "Too many requests. Wait a moment and try again.",
  500: "Something went wrong on our end. Try again shortly.",
  502: "A dependent service is temporarily unavailable.",
  503: "The service is temporarily unavailable.",
};

const GENERIC_BACKEND_MESSAGES = new Set([
  "unauthorized",
  "forbidden",
  "not found",
  "admin only",
  "insufficient org role",
  "internal server error",
  "fetch failed",
]);

// snake_case with at least one underscore: a machine code, not a sentence.
const MACHINE_CODE = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;

export function friendlyErrorMessage(status: number, body: unknown, text: string): string {
  const parsed = (body ?? {}) as { error?: unknown; message?: unknown; code?: unknown };
  const message = typeof parsed.message === "string" ? parsed.message : "";
  const error = typeof parsed.error === "string" ? parsed.error : "";
  const msg = message || error || (body === undefined ? text : "");

  const statusMessage = STATUS_MESSAGES[status] ?? "Something went wrong. Try again shortly.";
  if (!msg || GENERIC_BACKEND_MESSAGES.has(msg.toLowerCase())) return STATUS_MESSAGES[status] ?? (msg || `HTTP ${status}`);
  if (MACHINE_CODE.test(msg)) {
    const code = typeof parsed.code === "string" ? parsed.code : msg;
    return CODE_MESSAGES[code] ?? CODE_MESSAGES[msg] ?? statusMessage;
  }
  return msg;
}
